import { useEffect, useState } from 'react';
import { AlertTriangle, BookOpen, CheckCircle, RefreshCw, UserRound } from 'lucide-react';
import { authorSpotlightApi } from '../../../services/adminApi';

const AuthorSpotlight = () => {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState(null);

  const loadLists = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await authorSpotlightApi.getLists();
      setLists(result.lists || []);
    } catch (err) {
      console.error('Failed to load author spotlight lists:', err);
      setError(err.message || 'Failed to load author spotlight lists');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLists();
  }, []);

  const setActiveSpotlight = async (list) => {
    const nextValue = !list.is_author_spotlight;

    if (nextValue && !list.featured_book) {
      setError('Add at least one book before using a curated list as Discover Author.');
      return;
    }

    try {
      setSavingId(list.id);
      setError(null);
      await authorSpotlightApi.updateSpotlight(list.id, nextValue);
      await loadLists();
    } catch (err) {
      console.error('Failed to update author spotlight:', err);
      setError(err.message || 'Failed to update author spotlight');
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-text-primary mb-2">Discover Author</h1>
          <p className="text-text-secondary">Choose the global author spotlight for the mobile Home screen.</p>
        </div>
        <div className="bg-bg-surface rounded-lg border border-gray-200 p-12 text-center">
          <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading curated author lists...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary mb-2">Discover Author</h1>
          <p className="text-text-secondary">
            Pick one admin curated list to feature globally. The first book in the list becomes the Home card preview.
          </p>
        </div>
        <button
          onClick={loadLists}
          className="btn-secondary flex items-center space-x-2"
          disabled={savingId !== null}
        >
          <RefreshCw className="w-4 h-4" />
          <span>Refresh</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {lists.length === 0 ? (
        <div className="bg-bg-surface rounded-lg border border-gray-200 p-12 text-center">
          <UserRound className="w-12 h-12 text-text-secondary mx-auto mb-4" />
          <h3 className="font-semibold text-text-primary mb-2">No curated lists found</h3>
          <p className="text-text-secondary">Create an admin curated list with this author's books first.</p>
        </div>
      ) : (
        <div className="bg-bg-surface rounded-lg border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-text-secondary uppercase tracking-wide">
            <div className="col-span-4">Curated List</div>
            <div className="col-span-3">Featured Book</div>
            <div className="col-span-2">Books</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1 text-right">Action</div>
          </div>

          {lists.map((list) => (
            <div key={list.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-gray-100 last:border-b-0 items-center">
              <div className="col-span-4">
                <div className="font-medium text-text-primary">{list.name}</div>
                {list.description && (
                  <div className="text-sm text-text-secondary mt-1 line-clamp-2">{list.description}</div>
                )}
              </div>

              <div className="col-span-3 flex items-center space-x-3">
                {list.featured_book?.coverUrl ? (
                  <img
                    src={list.featured_book.coverUrl}
                    alt={list.featured_book.title}
                    className="w-10 h-14 object-cover rounded border border-gray-200"
                  />
                ) : (
                  <div className="w-10 h-14 rounded border border-gray-200 bg-gray-50 flex items-center justify-center">
                    <BookOpen className="w-4 h-4 text-text-secondary" />
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-sm font-medium text-text-primary truncate">
                    {list.featured_book?.title || 'No books yet'}
                  </div>
                  {list.featured_book?.author && (
                    <div className="text-xs text-text-secondary truncate">{list.featured_book.author}</div>
                  )}
                </div>
              </div>

              <div className="col-span-2 text-sm text-text-secondary">
                {list.book_count} {list.book_count === 1 ? 'book' : 'books'}
              </div>

              <div className="col-span-2">
                {list.is_author_spotlight ? (
                  <span className="inline-flex items-center space-x-1 px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3" />
                    <span>Active</span>
                  </span>
                ) : (
                  <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">Inactive</span>
                )}
              </div>

              <div className="col-span-1 flex justify-end">
                <button
                  onClick={() => setActiveSpotlight(list)}
                  disabled={savingId === list.id}
                  className={list.is_author_spotlight ? 'btn-secondary text-sm' : 'btn-primary text-sm'}
                >
                  {savingId === list.id
                    ? 'Saving...'
                    : list.is_author_spotlight
                      ? 'Clear'
                      : 'Use'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AuthorSpotlight;
